import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import {
  CustomDialog,
  StyledDataGrid,
} from "../../../components/StyledComponents";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useAuth } from "../../../context/AuthContext";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import { useSettings } from "../../../context/SettingsContext";
import { format } from "date-fns";
import { getUserList } from "../../../axios/User/UserApi";

const HistoryDialog = ({ onClose, requestHistory }) => {
  const { t } = useTranslation();
  const authCtx = useAuth?.(); // tránh lỗi call khi hook chưa sẵn
  const dateTimeFormat = authCtx?.dateTimeFormat;
  const authEmail = authCtx?.user?.email || "unknown";
  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
  const { settings } = useSettings();

  const handleCloseDialog = () => onClose();

  // Chuẩn hoá rows + parse newData 1 lần
  const rowsNormalized = useMemo(() => {
    const raw = Array.isArray(requestHistory)
      ? requestHistory
      : requestHistory?.data ?? [];

    return (raw || []).map((r) => {
      let nd = r?.newData;
      if (typeof nd === "string") {
        try {
          nd = JSON.parse(nd);
        } catch {
          nd = null;
        }
      }

      let direction = null;
      let eventTime = null;

      if (nd && typeof nd === "object") {
        // EDIT_EVENT_LOG
        if (r.requestTypeName === "EDIT_EVENT_LOG") {
          direction = nd.eventTypeId === 1 ? "CHECK_IN" : "CHECK_OUT";
          eventTime = nd.attendanceDateTime ?? null;
        }

        // ADD_EVENT_LOG (nếu có)
        else {
          if (nd.eventTypeId === 1) {
            direction = "CHECK_IN";
            eventTime = nd.timeCheckIn ?? null;
          } else if (nd.eventTypeId === 2) {
            direction = "CHECK_OUT";
            eventTime = nd.timeCheckOut ?? null;
          }
        }
      }

      return { ...r, __nd: nd, direction, eventTime };
    });
  }, [requestHistory]);

  const [allUser, setAllUser] = useState([]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const payload = {
          statusId: 1,
          page: 1,
          pageSize: 1000000,
          status: null,
          langId: localStorage.getItem("i18nextLngId")
        }
        const response = await getUserList(payload);
        const usersList = response.data.data;
        setAllUser(usersList);
      } catch (error) {
        console.log(error)
      }
    };
    fetchUser();

  }, []);

  const getFullNameByEmail = (email) => {
    if (!email) return "-";

    const normalizedEmail = String(email).trim().toLowerCase();

    const user = allUser.find(
      (u) =>
        u.email &&
        u.email.trim().toLowerCase() === normalizedEmail
    );

    // nếu tìm được thì trả fullName, không thì fallback về email
    return user?.fullName || email;
  };

  const columns = useMemo(
    () => [
      {
        field: "status",
        headerName: t("common.request-status"),
        width: 250,
        renderCell: (params) => {
          const status = params?.row?.requestStatusName || "";
          let color = "#6B7280";
          let label = status;
          switch (status) {
            case "APPROVED":
              color = "#22C55E";
              label = t("common.approval").toUpperCase();
              break;
            case "REJECTED":
              color = "#EF4444";
              label = t("common.rejected").toUpperCase();
              break;
            case "PENDING":
              color = "#EAB308";
              label = t("common.pending-request").toUpperCase();
              break;
          }
          return (
            <div className="flex items-center gap-1.5">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span style={{ color, fontWeight: 600, fontSize: 12 }}>
                {label}
              </span>
            </div>
          );
        },
      },
      {
        field: "requestTypeName",
        headerName: t("common.type"),
        rowSpanValueGetter: (_, row) => {
          if (!row.requestTypeName) return '';
          return t(`requestType.${row.requestTypeName}`, {
            defaultValue: row.requestTypeName.replace(/_/g, ' ')
          });
        },

        valueFormatter: (value) => {
          if (!value) return "-";
          return t(`requestType.${value}`, {
            defaultValue: value.replace(/_/g, ' ')
          });
        },
      },
      {
        field: "requestReason",
        headerName: t("common.requestReason"),
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.requestReason}` : value,
        valueFormatter: (value) =>
          value == null ? "No sensor data" : `${value}`,
      },
      {
        field: "responseReason",
        headerName: t("common.responseReason"),
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.responseReason}` : value,
        valueFormatter: (value) => (value == null ? t("common.not-processed-yet") : `${value}`),
      },
      {
        field: "createdTime",
        headerName: t("common.created-time"),
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.createdAt}` : value,
        valueFormatter: (value) => {
          if (!value) return "-";
          // API trả "YYYY-MM-DD HH:mm:ss"
          const formattedDate = format(new Date(value), dateTimeFormat);

          return formattedDate;
        },
        sortComparator: (a, b) => {
          const da = dayjs(a, "DD/MM/YYYY HH:mm:ss");
          const db = dayjs(b, "DD/MM/YYYY HH:mm:ss");
          if (!da.isValid() && !db.isValid()) return 0;
          if (!da.isValid()) return -1;
          if (!db.isValid()) return 1;
          return da.valueOf() - db.valueOf();
        },
      },
      // {
      //   field: "createdBy",
      //   headerName: t("common.processed-by"),
      //   rowSpanValueGetter: (value, row) =>
      //     row ? `${row.id}-${row.createdBy}` : value,
      //   valueFormatter: (value) => (value == null ? t("common.not-processed-yet") : `${value}`),
      //   renderCell: (params) => {
      //     const email = params.row?.createdBy;
      //     const fullName = getFullNameByEmail(email);
      //     return <span>{fullName}</span>;
      //   },
      // },
      {
        field: "direction",
        headerName: t('common.content-115'),
        renderCell: (params) => {
          const v = params?.row?.direction;
          if (v === "CHECK_IN") {
            return (
              <div
                className="text-green-500 flex items-center justify-start gap-1.5 uppercase"
                style={{ height: "100%" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                <p className="text-xs font-bold leading-5">
                  {t(`attendance.${v}`)}
                </p>
              </div>
            );
          }
          if (v === "CHECK_OUT") {
            return (
              <div
                className="text-blue-500 flex items-center justify-start gap-1.5 uppercase"
                style={{ height: "100%" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                <p className="text-xs font-bold leading-5">
                  {t(`attendance.${v}`)}
                </p>
              </div>
            );
          }
          return "-";
        },
        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${value}` : value,
      },

      {
        field: "eventTime",
        headerName: t('common.content-036'),

        valueGetter: (value, row) => {
          if (!row?.newData) return null;

          try {
            const parsed = JSON.parse(row.newData);
            return parsed.attendanceDateTime || null;
          } catch {
            return null;
          }
        },

        valueFormatter: (value) => {
          if (!value) return "-";
          return format(new Date(value), dateTimeFormat);
        },

        sortComparator: (a, b) => {
          const da = dayjs(a);
          const db = dayjs(b);

          if (!da.isValid() && !db.isValid()) return 0;
          if (!da.isValid()) return -1;
          if (!db.isValid()) return 1;

          return da.valueOf() - db.valueOf();
        },

        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${value}` : value,
      }
    ],
    [t, dateTimeFormat, settings.dateFormat, settings.timeFormat,]
  );

  const defaultColumnVisibility = {
    status: true,
    requestTypeName: true,
    requestReason: true,
    createdAt: false,
    responseReason: true,
    createdBy: false,
    direction: true,
    eventTime: true,
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    defaultColumnVisibility
  );

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    Cookies.set(`EventsGridInfoTable_${authEmail}`, JSON.stringify(newModel), {
      expires: 90,
      path: "/",
    });
  };

  useEffect(() => {
    const saved = Cookies.get(`EventsGridInfoTable_${authEmail}`);
    if (saved) {
      try {
        setColumnVisibilityModel(JSON.parse(saved));
      } catch {
        setColumnVisibilityModel(defaultColumnVisibility);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authEmail]);

  return (
    <CustomDialog
      open
      transitionDuration={300}
      maxWidth={"xl"}
      onClose={handleCloseDialog}
      keepMounted
    >
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("common.history")}</p>
          </div>
          <GrayIconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ overflow: "hidden", marginTop: "24px" }}>
        <StyledDataGrid
          rows={rowsNormalized}
          columns={columns}
          // Fallback id an toàn phòng khi rỗng id
          getRowId={(r) =>
            r?.id ?? r?.requestId ?? `${r?.createdAt}-${r?.createdBy}`
          }
          sx={{ height: "520px", width: "100%" }}
          localeText={tableTranslated}
          hideFooter
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
        />
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <CloseIcon />
            <p>{t("button.close")}</p>
          </ControlpanelSecondaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default HistoryDialog;
